import { Calendar, CalendarProps, Empty, Popover, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import getNudges from '../../../Utils/nudgeUtils';
import { getAllTransactions } from '../../../Utils/utils';
import TransactionCard from '../AllTransactions/TransactionCard';
import Nudges from '../Nudges';
import TopSummarySection from '../TopSummarySection';
import styles from './index.module.css';
export const SpendAnalyzerContext = React.createContext({});

const enum TransactionType {
  BIG_CREDIT,
  BIG_DEBIT,
  SMALL_DEBIT,
  SMALL_CREDIT,
  NORMAL,
  NO_TRANSACTION,
}

const getStyleClassFromTransactionType = (
  transactionType: TransactionType
): string => {
  switch (transactionType) {
    case TransactionType.NO_TRANSACTION:
      return styles.day_cell_no_txn;
    case TransactionType.BIG_CREDIT:
      return styles.day_cell_big_credit;
    case TransactionType.BIG_DEBIT:
      return styles.day_cell_big_debit;
    case TransactionType.SMALL_CREDIT:
      return styles.day_cell_normal;
    case TransactionType.SMALL_DEBIT:
      return styles.day_cell_normal;
  }
  return styles.day_cell;
};

type Props = {
  onBack?: () => void;
};

const CalendarView: React.FC<Props> = ({ onBack }) => {
  const allTxn = useMemo(() => {
    return getAllTransactions();
  }, []);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(new Date()));
  const [selectedDateTxns, setSelectedDateTxns] = useState<any[]>();

  const getTransactionTypeFromDate = (current: Dayjs): TransactionType => {
    const currentDateTxns = allTxn.filter((txn: any) => {
      return dayjs(txn['dateOfTransaction']).isSame(current, 'day');
    });
    if (currentDateTxns.length === 0) {
      return TransactionType.NO_TRANSACTION;
    }
    let maxDebitAmount = 0;
    let maxCreditAmount = 0;
    currentDateTxns.forEach((txn: any) => {
      if (typeof txn['amount'] === 'number') {
        if (txn['type'] === 'DEBIT') {
          maxDebitAmount = Math.max(maxDebitAmount, txn['amount']);
        } else {
          maxCreditAmount = Math.max(maxCreditAmount, txn['amount']);
        }
      }
    });

    if (maxCreditAmount >= maxDebitAmount) {
      if (maxCreditAmount > 1000) {
        return TransactionType.BIG_CREDIT;
      } else return TransactionType.SMALL_CREDIT;
    } else {
      if (maxDebitAmount > 1000) {
        return TransactionType.BIG_DEBIT;
      } else return TransactionType.SMALL_DEBIT;
    }
  };

  const getInfoMsgFromTransactionType = (txnType: TransactionType): string => {
    switch (txnType) {
      case TransactionType.BIG_CREDIT:
        return 'Large Credit Transactions';
      case TransactionType.BIG_DEBIT:
        return 'Large Debit Transactions';
      case TransactionType.SMALL_CREDIT:
        return 'Normal Expenses';
      case TransactionType.SMALL_DEBIT:
        return 'Normal Expenses';
    }
    return 'No transactions';
  };

  const cellRender: CalendarProps<Dayjs>['fullCellRender'] = (
    current,
    info
  ) => {
    if (info.type === 'date') {
      const txnType = getTransactionTypeFromDate(current);
      const infoMsg = getInfoMsgFromTransactionType(txnType);
      const isOtherMonthsDay = !current.isSame(selectedDate, 'M');

      return React.cloneElement(info.originNode, {
        ...info.originNode.props,
        children: (
          <Popover
            style={{ height: 10 }}
            content={() => {
              return (
                <p style={{ color: '#474747', fontSize: 12, padding: 0 }}>
                  {infoMsg}
                </p>
              );
            }}
          >
            <div
              className={[
                styles.day_cell,
                getStyleClassFromTransactionType(txnType),
                ,
                isOtherMonthsDay ? styles.day_cell_other_month : '',
              ].join(' ')}
            >
              <span>{current.date()}</span>
            </div>
          </Popover>
        ),
      });
    }
    return info.originNode;
  };

  const onSelectedDateChange = (date: Dayjs) => {
    setSelectedDate(date);
    const filteredTxns = allTxn.filter((txn: any) => {
      const transactionDate = dayjs(new Date(txn['dateOfTransaction']));
      return transactionDate.isSame(date, 'day');
    });
    setSelectedDateTxns(filteredTxns);
  };

  useEffect(() => {
    onSelectedDateChange(dayjs(allTxn.slice(-1)[0].dateOfTransaction));
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {onBack && (
        <TopSummarySection
          isBackButton={true}
          onBack={onBack}
          subTitle={''}
          showBigTitle={true}
          hideCarousel={true}
          title='Calendar View'
        />
      )}

      {/* Custom Header */}
      {/* <div style={{
            display : 'flex',
            width : '100%',
            color: '#0460ff'
        }}>
            <LeftOutlined
                style={{
                  left: 15,
                  top: 15,
                  fontSize : 20,
                  fontWeight : 800,
                  color: '#0460ff'
                }}
                onClick={onBack}
              />
              <div style={{
                display : 'flex',
                flexDirection : 'column',
                justifyContent : 'center',
                alignItems : 'center',
                justifySelf : 'center', flex : 1,
                gap : 0
              }}>
                <Typography.Text  style={{textAlign : 'center', fontWeight : 600, color: '#0460ff', fontSize: 28}}>Calendar View</Typography.Text>
                <Typography.Text style={{fontSize : 12, color: '#0460ff'}}>Know your spending</Typography.Text>
            </div>
        </div> */}
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 20,
        }}
      >
        <div
          style={{
            maxWidth: 425,
            padding: onBack ? '0px 24px' : '5px',
            boxSizing: 'border-box',
          }}
        >
          <Calendar
            defaultValue={dayjs(allTxn.slice(-1)[0].dateOfTransaction)}
            onSelect={onSelectedDateChange}
            fullCellRender={cellRender}
            fullscreen={false}
          />
        </div>
      </div>
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 8,
        }}
      >
        <div
          style={{
            maxWidth: 425,
            padding: onBack ? '0px 24px' : '5px',
            boxSizing: 'border-box',
          }}
        >
          <Nudges nudgeList={getNudges('calendar')} />
        </div>
      </div>
      <div
        style={{
          marginTop: '10px',
          marginBottom: 7,
          padding: onBack ? '0 24px' : 0,
        }}
      >
        <Typography.Text style={{ fontSize: '1.5em', fontWeight: 400 }}>
          {selectedDate.format('dddd') +
            ' ' +
            selectedDate.date() +
            ' ' +
            selectedDate.format('MMMM')}
        </Typography.Text>
        {selectedDateTxns && selectedDateTxns.length > 0 ? (
          selectedDateTxns.map((transaction: any, index: number) => {
            return (
              <TransactionCard
                transaction={transaction}
                isLastTransaction={selectedDateTxns.length - 1 != index}
              />
            );
          })
        ) : (
          <Empty
            description={
              <Typography.Text>No transaction found</Typography.Text>
            }
          />
        )}
      </div>
    </div>
  );
};

export default CalendarView;
